import { useState, useEffect, useRef } from 'react'
import Switch from '../../../components/Switch'
import {
  INVOICE_STATUS_ID,
  isInvoicePaid,
} from '../../../utils/invoiceStatus'

const disabledFieldStyle = {
  background: '#f9fafb',
  color: '#6b7280',
  cursor: 'not-allowed',
}

const Label = ({ children }) => (
  <label
    className="form-label mb-1"
    style={{
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: '#6b7280',
    }}
  >
    {children}
  </label>
)

const EditInvoiceModal = ({ show, invoice, onClose, onSave, isSaving = false }) => {
  const fileInputRef = useRef(null)
  const [form, setForm] = useState({
    invoiceNumber: '',
    description: '',
    dueDate: '',
    amount: '',
    pending: true,
  })
  const [file, setFile] = useState(null)
  const [fileError, setFileError] = useState('')

  useEffect(() => {
    if (invoice) {
      setForm({
        invoiceNumber: invoice.invoiceNumber ?? '',
        description: invoice.description ?? '',
        dueDate: invoice.due_date ?? '',
        amount: invoice.total_amount ?? '',
        pending: !isInvoicePaid(invoice),
      })
      setFile(null)
      setFileError('')
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [invoice])

  const assignFile = (selectedFile) => {
    if (!selectedFile) return
    if (selectedFile.type !== 'application/pdf') {
      setFileError('Solo se permiten archivos PDF.')
      setFile(null)
      return
    }
    setFileError('')
    setFile(selectedFile)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!invoice?.id) return

    const status = form.pending
      ? INVOICE_STATUS_ID.NO_PAGADA
      : INVOICE_STATUS_ID.PAGADA
    onSave({
      id: invoice.id,
      status,
      invoiceNumber: form.invoiceNumber,
      file,
    })
  }

  if (!show || !invoice) return null

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1050 }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSaving) onClose()
      }}
    >
      <div
        className="bg-white rounded shadow"
        style={{
          width: '100%',
          maxWidth: '520px',
          maxHeight: '90vh',
          overflowY: 'auto',
          margin: '16px',
        }}
      >
        <div
          className="d-flex justify-content-between align-items-center px-4 py-3"
          style={{ borderBottom: '1px solid #f3f4f6' }}
        >
          <div>
            <p className="mb-0" style={{ fontWeight: '700', fontSize: '15px', color: '#111827' }}>
              Editar factura
            </p>
            <p className="mb-0 mt-1" style={{ fontSize: '12px', color: '#9ca3af' }}>
              Cliente:{' '}
              <strong style={{ color: '#374151' }}>
                {invoice.companyName || invoice.client || '—'}
              </strong>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            style={{
              background: 'none',
              border: 'none',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              fontSize: '20px',
              color: '#6b7280',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 d-flex flex-column gap-3">
          <p className="mb-0" style={{ fontSize: '13px', color: '#6b7280' }}>
            Puedes modificar el NRO, el estado y cargar una nueva factura. El resto de campos es de solo lectura.
          </p>

          <div>
            <Label>NRO</Label>
            <input
              type="text"
              className="form-control"
              value={form.invoiceNumber}
              onChange={(e) =>
                setForm((f) => ({ ...f, invoiceNumber: e.target.value }))
              }
            />
          </div>

          <div>
            <Label>Descripción</Label>
            <input
              type="text"
              className="form-control"
              value={form.description}
              disabled
              readOnly
              style={disabledFieldStyle}
            />
          </div>

          <div>
            <Label>Estado</Label>
            <div className="d-flex align-items-center gap-2">
              <Switch
                isCheked={!form.pending}
                changeStatus={() =>
                  setForm((f) => ({ ...f, pending: !f.pending }))
                }
              />
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  lineHeight: 1,
                }}
              >
                {form.pending ? 'Pendiente' : 'Pagado'}
              </span>
            </div>
          </div>

          <div>
            <Label>Factura (PDF)</Label>
            <div
              className="rounded p-3 text-center"
              style={{
                border: '2px dashed #d1d5db',
                background: '#f9fafb',
                cursor: isSaving ? 'not-allowed' : 'pointer',
              }}
              onClick={() => {
                if (!isSaving) fileInputRef.current?.click()
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="d-none"
                onChange={(e) => assignFile(e.target.files?.[0])}
                disabled={isSaving}
              />
              <p className="mb-0" style={{ fontSize: '13px', color: '#6b7280' }}>
                Haz clic para seleccionar una nueva factura PDF (opcional)
              </p>
              {file && (
                <p className="mb-0 mt-2" style={{ fontSize: '13px', color: '#111827', fontWeight: 600 }}>
                  Archivo seleccionado: {file.name}
                </p>
              )}
            </div>
            {fileError && (
              <span className="text-danger" style={{ fontSize: '12px' }}>
                {fileError}
              </span>
            )}
          </div>

          <div className="row g-3">
            <div className="col-6">
              <Label>Importe (€)</Label>
              <input
                type="number"
                className="form-control"
                value={form.amount}
                disabled
                readOnly
                style={disabledFieldStyle}
              />
            </div>
            <div className="col-6">
              <Label>Vencimiento</Label>
              <input
                type="date"
                className="form-control"
                value={form.dueDate}
                disabled
                readOnly
                style={disabledFieldStyle}
              />
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="btn btn-white border border-on-bg-primary px-4"
              style={{ fontSize: '14px' }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="btn btn-bg-CTA-primary px-4"
              style={{ fontSize: '14px' }}
            >
              {isSaving ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditInvoiceModal
